<template>
	<div>
		<div v-if="!reloading && !nothingToSimulate && !isBlendedFerment">
			<p v-if="hasEnoughBaume()">
				<span v-if="simulation.status == 1" class="label label-lg label-success">
					<i class="fa fa-check"></i> Good
				</span>
				<span v-if="simulation.status == 10" class="label label-lg label-info">
					<i class="fa fa-exclamation-triangle"></i> Low Range On Spec
				</span>
				<span v-if="simulation.status == 20" class="label label-lg label-danger">
					<i class="fa fa-exclamation-triangle"></i> High Range On Spec
				</span>
				<span v-if="simulation.status == 30" class="label label-lg label-info">
					<i class="fa fa-exclamation-triangle"></i> Sluggish Fermentation
				</span>
				<span v-if="simulation.status == 40" class="label label-lg label-danger">
					<i class="fa fa-exclamation-triangle"></i> Rapid Fermentation
				</span>

				<span v-if="simulation.prediction_score > 25" class="label label-lg label-warning" style="margin-left: 6px">
					<i class="fa fa-exclamation-triangle"></i> Poor Simulation
				</span>
			</p>
			<p v-else>
				<span class="label label-lg label-warning">
					<i class="fa fa-exclamation-triangle"></i> Not Enough Data
				</span>
			</p>

			<p v-if="!hasEnoughBaume()" class="text-center">Need more Baume measurements (at least 4) to make a prediction.</p>
		</div>

		<p>
			<a class="pull-right fa fa-question-circle popover-help"
				tabindex="0"
				role="button"
				data-container="body"
				data-placement="left"
				data-html="true"
				data-trigger="focus"
				title="Help"
				:data-content="popoverContent">
			</a>
		</p>

		<div v-if="reloading" class="loading-graph">
			<div class="status-loading"></div>
		</div>

		<canvas v-if="!reloading && !nothingToSimulate && !unableToSimulate && !failedToSimulate" class="page-graph" id="ferment-graph" height="200"></canvas>

		<div v-if="!reloading">
			<div v-if="nothingToSimulate">
				<h4>Nothing to simulate. Please add ferment data.</h4>
			</div>

			<div v-if="unableToSimulate">
				<h4>Unable to simulate. Not enough ferment data.</h4>
			</div>

			<div v-if="failedToSimulate">
				<h4>The simulation has failed.</h4>
			</div>
		</div>

		<div v-if="!checklistCompleted && !isBlendedFerment">
			<h4>Simulation Checklist</h4>
			<div class="checklist">
				<checklist-item v-for="list in checklist" :exists="list.exists" track-by="message">
					{{list.message}}
				</check-list-item>
			</div>
		</div>

		<div class="margin-20"></div>

		<div v-if="!reloading && !nothingToSimulate && !unableToSimulate && !failedToSimulate && !isBlendedFerment">
			<div class="margin-20"></div>

			<div class="row">
				<div class="col-xs-4">
					Sugars: {{ getLastSugar() }} {{{ getSugarUnit() }}}
				</div>
				<div class="col-xs-4">
					Temperature: {{ getLastTemperature() }} {{{ getTemperatureUnit() }}}
				</div>
				<div v-if="getPredictedPeakRate()" class="col-xs-4">
					Predicted Peak Rate: {{ getPredictedPeakRate() }}
				</div>
			</div>

			<div class="row">
				<div class="col-xs-4">
					Predicted Duration: {{ getPredictedDuration() }} days
				</div>
				<div class="col-xs-4">
					Predicted End Date: {{ getPredictedEndDate() }}
				</div>
				<div class="col-xs-4">
					Predicted Peak Date: {{ getPredictedPeakDate() }}
				</div>
			</div>

			<div class="margin-20"></div>
		</div>

		<div class="row" v-if="$route.name == 'ferments.index' && !nothingToSimulate && !isBlendedFerment">
			<div class="col-sm-4">
				<button class="btn btn-primary btn-lg btn-block" :disabled="simulatorRunning" @click.prevent="$emit('reloadGraph', true)">Run Simulation</button>
			</div>
			<div class="col-sm-4">
				<a v-link="{name: 'ferments.what-now', params: { fermenterId: fermenter.id, fermentId: ferment.id }}" class="btn btn-lg btn-block btn-primary">What Now?</a>
			</div>
			<div class="col-sm-4">
				<a v-link="{name: 'ferments.what-if', params: { fermenterId: fermenter.id, fermentId: ferment.id }}" class="btn btn-lg btn-block btn-primary">What If?</a>
			</div>
		</div>

		<div v-if="$route.name == 'ferments.what-if' && !nothingToSimulate">
			<what-if></what-if>
		</div>

		<div v-if="$route.name == 'ferments.what-now' && !nothingToSimulate">
			<what-now></what-now>
		</div>
	</div>
</template>

<script>
	import { Ferments as FermentActions  } from 'vx/actions'
	import { site, simulatorSetup as simulator, ferment, fermenter, simulatorRunning } from 'vx/getters'
	import Vue from 'vue'
	import Chart from 'chart.js'
	import moment from 'moment'
	import * as types from 'vx/mutation-types'
	import Notify from 'utils/notify'
	import units from 'utils/units'
	import WhatNow from './what-now'
	import WhatIf from './what-if'
	import ChecklistItem from './checklist-item'

	const MIN_BAUME = 4;

	export default {
		components: {
			WhatNow,
			WhatIf,
			ChecklistItem
		},

		vuex: {
			getters: {
				site,
				simulator,
				ferment,
				fermenter,
				simulatorRunning
			},

			actions: {
				runSimulation: FermentActions.simulation,
				runTemporarySimulation: FermentActions.temporarySimulation
			}
		},

		data() {
			return {
				chart: null,
				baselineSimulation: null,
				simulation: {},
				reloading: false,
				failedToSimulate: false,
				nothingToSimulate: false,
				unableToSimulate: false,
				simulationNotification: null,
				unableToSimulateMessage: '',
				moment
			}
		},

		events: {
			generateBase64() {
				if (! this.chart) return;

				this.$store.dispatch(types.SET_GRAPH_BASE64, this.chart.toBase64Image());
			},

			buildGraph(target) {
				this.buildGraph(target);
			},

			reloadGraph(force = false, data = {}) {
				this.loadGraph(force, data);
			},

			clearGraphCountdown() {
				if (this.simulationNotification) {
					Notify.clear(this.simulationNotification);
				}
			},

			reloadGraphCountdown(seconds) {
				if (this.unableToSimulateWithData()) {
					this.nothingToSimulate = false;
					this.unableToSimulate = true;

					return;
				}

				if (this.isBlendedFerment) {
					return;
				}

				if (this.simulationNotification) {
					Notify.clear(this.simulationNotification);
				}

				this.simulationNotification = Notify.info(`Simulation running in ${seconds} seconds.`, { timeout: (seconds * 1000) });
			},

			clearGraph() {
				if (this.chart) {
					this.chart.clear();
				}

				this.nothingToSimulate = true;
				this.unableToSimulate = false;
				this.failedToSimulate = false;
			},

			destroyGraph() {
				if (this.chart) {
					this.chart.destroy();

					this.chart = null;
				}

				this.nothingToSimulate = true;
				this.unableToSimulate = false;
				this.failedToSimulate = false;
			}
		},

		computed: {
			sugarLabel () {
				return this.simulator.sugar_units
					? 'Baume'
					: 'Brix'
			},

			temperatureLabel () {
				return this.simulator.temp_units
					? 'Temperature (°C)'
					: 'Temperature (°F)'
			},

			fermentRateLabel () {
				return this.simulator.sugar_units
					? 'Ferment Rate (Be/day)'
					: 'Ferment Rate (Bx/day)'
			},

			popoverContent () {
				if (this.isBlendedFerment) {
					return '<small>This graph shows the sugar readings of the blend components (before blending) in light colours, and the sugar readings of the final blend in dark blue. You can show/hide individual series by clicking on them in the legend.</small>'
				}

				return '<small>The graph shows the predicted profile of the ferment from the simulator. Individual series can be shown/hidden by clicking on them in the legend.</small>'
			},

			checklist() {
				function makeItem(obj, message) {
					return { exists: !!obj, message }
				}

				const ferment = this && this.ferment;

				return ferment
					? [
							makeItem(ferment.wine_name, 'Wine Name'),
							makeItem(ferment.start_date, 'Wine Start Date'),
							makeItem(ferment.juiceAnalysis.data.yan, 'Juice Analysis: YAN'),
							makeItem(ferment.duration, 'Ferment Desired Duration'),
							makeItem(ferment.volume, 'Ferment Volume'),
							makeItem(ferment.dailyData.data[0] && ferment.dailyData.data[0].baume, 'Initial Baume'),
							makeItem(ferment.dailyData.data[0] && ferment.dailyData.data[0].temp, 'Initial Temperature')
						]
					: [];
			},

			checklistCompleted() {
				const checklist = this.checklist;

				for (let prop in checklist) {
					if (checklist.hasOwnProperty(prop) && !checklist[prop].exists) {
						return false;
					}
				}

				return true;
			},

			isBlendedFerment () {
				return this.ferment &&
					this.ferment.blendComponents &&
					this.ferment.blendComponents.data.length > 0
			}
		},

		methods: {
			getLastIndex() {
				let lastIndex = -1;

				for (let i = 0; i < this.ferment.dailyData.data.length; ++i) {
					if (this.ferment.dailyData.data[i].baume) {
						lastIndex = i;
					}
				}

				return lastIndex;
			},

			/**
			 * Get the last temperature and convert it to appropriate unit.
			 *
			 * @return float
			 */
			getLastTemperature() {
				let lastIndex = this.getLastIndex();

				if (lastIndex < 0) {
					return 'n/a';
				}

				if (this.ferment.dailyData.data[lastIndex].temp) {
					return parseFloat(this.ferment.dailyData.data[lastIndex].temp).toFixed(1);
				} else if (this.simulation.model) {
					return units.celsiusToFahrenheit(parseFloat(this.simulation.model.model_temperature[lastIndex].value)).toFixed(1);
				}

				return 'n/a';
			},

			/**
			 * Get the temperature units.
			 *
			 * @return string
			 */
			getTemperatureUnit() {
				return units.temperatureUnit();
			},

			/**
			 * Get the last sugar and convert it to appropriate unit.
			 *
			 * @return float
			 */
			getLastSugar() {
				let lastIndex = this.getLastIndex();

				if (lastIndex < 0) {
					return 'n/a';
				}

				return units.baumeToBrix(parseFloat(this.ferment.dailyData.data[lastIndex].baume)).toFixed(2);
			},

			/**
			 * Get sugar unit.
			 *
			 * @return string
			 */
			getSugarUnit() {
				return units.sugarUnit();
			},

			/**
			 * Get the predicted duration.
			 *
			 * @return int
			 */
			getPredictedDuration() {
				return this.simulation.completion_model;
			},

			/**
			 * Get the predicted end date.
			 *
			 * @return string
			 */
			getPredictedEndDate() {
				return moment(this.ferment.start_date, 'DD/MM/YYYY hh:mm A').add(this.simulation.completion_model, 'days').format('DD/MM/YYYY');
			},

			getPredictedPeakDate() {
				return moment(this.ferment.start_date, 'DD/MM/YYYY hh:mm A').add(this.simulation.peak_rate_day, 'days').format('DD/MM/YYYY');
			},

			getPredictedPeakRate() {
				if (this.simulation && this.simulation.peak_rate) {
					return units.baumeToBrix(this.simulation.peak_rate).toFixed(2);
				}

				return 0
			},

			getBaumeCount() {
				let baumeCount = 0;

				for (let i = 0; i < this.ferment.dailyData.data.length; ++i) {
					if (this.ferment.dailyData.data[i].baume) {
						baumeCount++;
					}
				}

				return baumeCount;
			},

			hasEnoughBaume() {
				return this.getBaumeCount() >= MIN_BAUME;
			},

			unableToSimulateWithData() {
				// If the ferment has no daily data then we are unable to simulate
				if (! this.ferment.dailyData.data) {
					this.unableToSimulateMessage = 'Ferment daily data are missing.'
					return true;
				}

				// If the ferment does not have a desired duration then we are unable to simulate
				if (! this.ferment.duration) {
					this.unableToSimulateMessage = 'Ferment desired duration is missing.'
					return true;
				}

				// If the ferment does not have a volume then we are unable to simulate.
				if (! this.ferment.volume) {
					this.unableToSimulateMessage = 'Ferment volume is missing.'
					return true;
				}

				// If the ferment has less than 4 points of actual entered data, this means we
				// need at least 4 baume entries. we can still simulate it but the
				// prediction curve will be hidden.
				if (! this.hasEnoughBaume()) {
					this.unableToSimulateMessage = 'At least 4 baume entries are needed for prediction.'
				} else {
					this.unableToSimulateMessage = '';
				}

				return false;
			},

			loadGraph(force = false, ferment = {}) {
				if (this.chart) {
					this.chart.destroy();

					this.chart = null;
				}

				this.$store.dispatch(types.SET_SIMULATOR_RUNNING, true);
				this.reloading = true;
				this.nothingToSimulate = false;
				this.unableToSimulate = false;

				// If the ferment has not been simulated then we want to force the simulation
				// to run so we can get an updated simulation.
				if (! this.ferment.simulated) {
					force = true;
				}

				// Fire off the clear simulate timeout event which clears the countdown from
				// the notifications window.
				this.$dispatch('clearSimulateTimeout');

				Vue.nextTick(() => {
					$('.loading-graph').css('height', Math.ceil($('.loading-graph').width() / 1.501408451)+'px');
				});

				if (! this.ferment.id) {
					this.reloading = false;
					this.nothingToSimulate = true;

					this.$store.dispatch(types.SET_SIMULATOR_RUNNING, false);

					return;
				} else if (this.isBlendedFerment) {
					this.reloading = false;
					this.unableToSimulate = false;
					this.$store.dispatch(types.SET_SIMULATOR_RUNNING, false);

					Vue.nextTick(() => {
						this.buildGraph('ferment-graph');
					});

					return;
				} else if (this.unableToSimulateWithData()) {
					this.reloading = false;
					this.unableToSimulate = true;

					this.$store.dispatch(types.SET_SIMULATOR_RUNNING, false);

					return;

				// If we do have a ferment and we are on the what if or the what now route then
				// we want to get the data required and then hit the temporary simulation
				// endpoint to generate simulation data using the ferment and the
				// updated data.
				} else if (this.ferment.id && this.$route.name == 'ferments.what-if') {
					return this.runTemporarySimulation(this.site.id, this.ferment.id, {
						fresh: 'yes',
						ferment: {
							duration: this.ferment.duration,
							climate: this.ferment.climate,
							style: this.ferment.style,
							volume: this.ferment.volume,
							initial_cell_mass: this.ferment.initial_cell_mass,
							total_water_added: this.ferment.total_water_added,
							dailyData: this.ferment.dailyData
						}
					}).then(this.fetchBaselineSimulation, this.handleFailedSimulationResponse);
				} else if (this.ferment.id && this.$route.name == 'ferments.what-now' && ferment.id) {
					return this.runTemporarySimulation(
						this.site.id, this.ferment.id, { fresh: 'yes', ferment }
					).then(
						this.fetchBaselineSimulation,
						this.handleFailedSimulationResponse
					);
				}

				return this.runSimulation(this.site.id, this.ferment.id, force).then(this.handleSimulationResponse, this.handleFailedSimulationResponse);
			},

			fetchBaselineSimulation(response) {
				// If we haven't fetched the baseline simulation data yet for a "what if" then
				// we'll fire off that request first and then we'll handle the initial response.
				if (! this.baselineSimulation) {
					this.runSimulation(this.site.id, this.ferment.id, false).then(baselineResponse => {
						this.baselineSimulation = baselineResponse.body.data;

						this.handleSimulationResponse(response);
					}, this.handleFailedSimulationResponse);
				} else {
					this.handleSimulationResponse(response);
				}
			},

			handleFailedSimulationResponse(response) {
				this.simulation = {};

				this.reloading = false;
				this.nothingToSimulate = false;
				this.unableToSimulate = false;
				this.failedToSimulate = true;

				this.$store.dispatch(types.SET_SIMULATOR_RUNNING, false);
			},

			handleSimulationResponse(response) {
				if (! response.body.data) {
					this.handleFailedSimulationResponse(response);

					return;
				}

				this.simulation = response.body.data;

				this.reloading = false;
				this.nothingToSimulate = false;
				this.unableToSimulate = false;
				this.failedToSimulate = false;

				this.$store.dispatch(types.SET_SIMULATOR_RUNNING, false);

				Vue.nextTick(() => {
					this.buildGraph('ferment-graph');
				});
			},

			buildGraph(target) {
				if (! document.getElementById(target) || this.reloading) return;

				let datasets = this.getDataSets(),
					labels = this.getLabels(),
					canvas = document.getElementById(target),
					context = canvas.getContext('2d');

				this.chart = new Chart(context, {
					type: 'line',
					data: {
						labels,
						datasets
					},
					options: {
						animation: {
							duration: 0,
							onComplete() {
								let globalCompositeOperation = context.globalCompositeOperation;

								context.globalCompositeOperation = 'destination-over';
								context.fillStyle = '#ffffff';
								context.fillRect(0, 0, canvas.width, canvas.height);

								context.globalCompositeOperation = globalCompositeOperation;
							}
						},
						responsive: true,
						maintainAspectRatio: true,
						tooltips: {
							mode: 'label'
						},
						hover: {
							mode: 'dataset'
						},
						scales: {
							xAxes: [{
								display: true,
								ticks: {
									callback(value, index, values) {
										if (value == 0) {
											return value;
										}

										return value % 5 == 0 ? value : null;
									},
									maxRotation: 0
								},
								gridLines: {
									display: true
								},
								scaleLabel: {
									display: true,
									labelString: 'Ferment Duration (days)'
								}
							}],
							yAxes: [
								{
									id: 'y1',
									display: true,
									scaleLabel: {
										display: true,
										labelString: this.isBlendedFerment
											? `${this.sugarLabel}, ${this.temperatureLabel}`
											: `${this.sugarLabel}, Active Cell Mass (g/L), ${this.temperatureLabel}`
									},
									gridLines: {
										display: true
									},
									ticks: {
										suggestedMin: -2
									}
								},
								{
									id: 'y2',
									display: !this.isBlendedFerment,
									scaleLabel: {
										display: true,
										labelString: this.fermentRateLabel
									},
									ticks: {
										beginAtZero: true
									},
									position: 'right',
									gridLines: {
										display: false
									}
								}
							]
						}
					}
				});
			},

			getXAxisLengthFromBlendComponents () {
				return _.maxBy(this.ferment.dailyData.data, 'day').day
			},

			getEarliestStartDate () {
				return moment(this.ferment.start_date, 'DD/MM/YYYY h A')
			},

			getLabelsFromBlendComponents () {
				let xAxisLength = this.getXAxisLengthFromBlendComponents()

				return this.ferment.dailyData.data.filter(point => point.day <= xAxisLength).map(point => {
					return point.day;
				});
			},

			getDataSetsFromBlendComponents () {
				let datasets = [];
				let xAxisLength = this.getXAxisLengthFromBlendComponents()
				let colors = [
					'rgb(90, 146, 237, 0.3)',
					'rgb(221, 184, 70, 0.3)',
					'rgb(234, 99, 21, 0.3)',
					'rgb(100, 153, 16, 0.3)',
					'rgb(13, 184, 196, 0.3)',
					'rgb(121, 33, 198, 0.3)',
					'rgb(193, 66, 52, 0.3)',
				]

				const randomRgb = (alpha = 1) => {
					let o = Math.round, r = Math.random, s = 255;
					return `rgba(${o(r()*s)},${o(r()*s)},${o(r()*s)},${alpha})`;
				}

				let measuringFrequency = (1 / this.ferment.measuring_freq_integer)

				const filterFermentDailyData = (ferment) => {
					// Determine the offset for this ferment by getting the earliest start date from the
					// blend components and performing a date diff.
					let indexOffset = Math.floor(
						moment(ferment.start_date, 'DD/MM/YYYY h A').diff(this.getEarliestStartDate(), 'hours') / 24 / measuringFrequency
					)

					let data = ferment.dailyData.data.filter((point, index) => {
						if (index + indexOffset >= 0) {
							return true
						}

						return false
					}).map(point => {
						if (point.baume !== null && !point.locked) {
							return parseFloat(point.baume).toFixed(4);
						}

						return null;
					})

					// For positive offset, add X amount of data points to the beginning of the dataset based on the index offset.
					if (indexOffset > 0) {
						data.unshift(..._.fill(Array(indexOffset), null))
					}

					return data
				};

				datasets.push({
					label: 'Temperature',
					data: this.ferment.dailyData.data.map(data => {
						return data.temp ? data.temp : null;
					}),
					fill: false,
					borderColor: 'rgba(234, 72, 72, 0.8)',
					backgroundColor: 'rgba(234, 72, 72, 0.8)',
					pointBorderColor: 'rgba(234, 72, 72, 0.8)',
					pointBackgroundColor: 'rgba(234, 72, 72, 0.8)',
					pointRadius: 0,
					pointHoverRadius: 0,
					yAxisID: 'y1',
					spanGaps: true,
					lineTension: 0.20
				}, {
					label: `${this.ferment.wine_name}`,
					data: filterFermentDailyData(this.ferment),
					fill: false,
					borderColor: 'rgb(65, 124, 226, 0.8)',
					backgroundColor: 'rgb(65, 124, 226, 0.8)',
					pointBorderColor: 'rgb(65, 124, 226, 0.8)',
					pointBackgroundColor: 'rgb(65, 124, 226, 0.8)',
					pointBorderWidth: 3,
					showLine: false,
					pointStyle: 'circle',
					spanGaps: true,
					yAxisID: 'y1'
				})

				_.each(this.ferment.blendComponents.data, (ferment, index) => {
					let color = index in colors ? colors[index] : randomRgb(0.3)

					datasets.push({
						label: `ex ${ferment.fermenter.data.name}`,
						data: filterFermentDailyData(ferment).map(units.baumeToBrix),
						fill: false,
						borderColor: color,
						backgroundColor: color,
						pointBorderColor: color,
						pointBackgroundColor: color,
						pointBorderWidth: 3,
						showLine: false,
						pointStyle: 'circle',
						spanGaps: true,
						yAxisID: 'y1'
					})
				});

				return datasets
			},

			getLabels() {
				if (this.isBlendedFerment) {
					return this.getLabelsFromBlendComponents()
				}

				let data,
					xAxisLength = this.getXAxisLength();

				if (this.baselineSimulation && this.baselineSimulation.model.baume.slice(-1)[0].day < this.simulation.model.baume.slice(-1)[0].day) {
					data = this.baselineSimulation;
				} else {
					data = this.simulation;
				}

				return data.model.baume.filter(point => point.day <= xAxisLength).map(point => {
					return point.day;
				});
			},

			getDataSets() {
				let datasets = [];

				if (this.isBlendedFerment) {
					return this.getDataSetsFromBlendComponents();
				}

				// If we are on the "what if" or "what now" page and we have a baseline simulation then we'll
				// go ahead and add all the baseline simulation data to the chart. This appears
				// as a sort of "ghost layer" that basically let's the user see how things
				// have changed as they adjust the "what if" data.
				if ((this.$route.name == 'ferments.what-if' || this.$route.name == 'ferments.what-now') && this.baselineSimulation) {
					if (this.$route.name == 'ferments.what-if') {
						// Add baseline actual baume to the chart.
						datasets.push({
							label: `Baseline ${this.sugarLabel} (Actual)`,
							data: this.getDataFromSimulation('baume_actual', this.baselineSimulation).map(units.baumeToBrix),
							fill: false,
							borderColor: 'rgba(106, 133, 145, 0.3)',
							backgroundColor: 'rgba(106, 133, 145, 0.3)',
							pointBorderColor: 'rgba(106, 133, 145, 0.3)',
							pointBackgroundColor: 'rgba(106, 133, 145, 0.3)',
							pointBorderWidth: 5,
							showLine: false,
							pointStyle: 'rectRot',
							spanGaps: true,
							yAxisID: 'y1'
						},
						{
							label: 'Ferment Rate',
							data: this.getDataFromSimulation('ferment_derivative').map(units.baumeToBrix),
							fill: false,
							borderColor: 'rgba(153, 120, 188, .8)',
							backgroundColor: 'rgba(153, 120, 188, .8)',
							pointBorderColor: 'rgba(153, 120, 188, .8)',
							pointBackgroundColor: 'rgba(153, 120, 188, .8)',
							pointRadius: 0,
							pointHoverRadius: 0,
							yAxisID: 'y2'
						});
					}

					// Add baseline predicted baume to the chart.
					datasets.push({
						label: `Baseline ${this.sugarLabel} (Predicted)`,
						data: this.getDataFromSimulation('baume', this.baselineSimulation).map(units.baumeToBrix),
						fill: false,
						borderColor: 'rgba(63, 132, 121, 0.3)',
						backgroundColor: 'rgba(63, 132, 121, 0.3)',
						pointBorderColor: 'rgba(63, 132, 121, 0.3)',
						pointBackgroundColor: 'rgba(63, 132, 121, 0.3)',
						pointBorderWidth: 1,
						pointRadius: 0,
						pointHoverRadius: 0,
						yAxisID: 'y1'
					});

					// Add baseline temperature to the chart.
					datasets.push({
						label: `Baseline ${this.temperatureLabel}`,
						data: this.getDataFromSimulation('corrected_temperature', this.baselineSimulation).map(units.celsiusToFahrenheit),
						fill: false,
						borderColor: 'rgba(234, 72, 72, 0.3)',
						backgroundColor: 'rgba(234, 72, 72, 0.3)',
						pointBorderColor: 'rgba(234, 72, 72, 0.3)',
						pointBackgroundColor: 'rgba(234, 72, 72, 0.3)',
						pointBorderWidth: 1,
						pointRadius: 0,
						pointHoverRadius: 0,
						yAxisID: 'y2'
					});
				}

				datasets.push({
					label: `${this.sugarLabel} (Actual)`,
					data: this.getDataFromSimulation('baume_actual').map(units.baumeToBrix),
					fill: false,
					borderColor: 'rgba(98, 141, 188, 0.9)',
					backgroundColor: 'rgba(98, 141, 188, 0.9)',
					pointBorderColor: 'rgba(98, 141, 188, 0.9)',
					pointBackgroundColor: 'rgba(98, 141, 188, 0.9)',
					pointBorderWidth: 7,
					showLine: false,
					pointStyle: 'rectRot',
					spanGaps: true,
					yAxisID: 'y1'
				},
				{
					label: this.temperatureLabel,
					data: this.getDataFromSimulation('corrected_temperature').map(units.celsiusToFahrenheit),
					fill: false,
					borderColor: 'rgba(234, 72, 72, 0.8)',
					backgroundColor: 'rgba(234, 72, 72, 0.8)',
					pointBorderColor: 'rgba(234, 72, 72, 0.8)',
					pointBackgroundColor: 'rgba(234, 72, 72, 0.8)',
					pointRadius: 0,
					pointHoverRadius: 0,
					yAxisID: 'y1'
				});

				// We need at least 4 baume entries for drawing prediction curve
				if (this.hasEnoughBaume()) {
					datasets.push({
						label: `${this.sugarLabel} (Predicted)`,
						data: this.getDataFromSimulation('baume').map(units.baumeToBrix),
						fill: false,
						borderColor: 'rgba(63, 132, 121, 0.9)',
						backgroundColor: 'rgba(63, 132, 121, 0.9)',
						pointBorderColor: 'rgba(63, 132, 121, 0.9)',
						pointBackgroundColor: 'rgba(63, 132, 121, 0.9)',
						pointBorderWidth: 1,
						pointRadius: 0,
						pointHoverRadius: 0,
						yAxisID: 'y1'
					});

					if (this.$route.name == 'ferments.index') {
						datasets.push({
							label: `${this.sugarLabel} (High)`,
							data: this.getDataFromSimulation('high_range').map(units.baumeToBrix),
							fill: false,
							borderColor: 'rgba(63, 132, 121, 0.4)',
							backgroundColor: 'rgba(63, 132, 121, 0.4)',
							pointBorderColor: 'rgba(63, 132, 121, 0.4)',
							pointBackgroundColor: 'rgba(63, 132, 121, 0.4)',
							pointBorderWidth: 1,
							pointRadius: 0,
							pointHoverRadius: 0,
							yAxisID: 'y1'
						},
						{
							label: `${this.sugarLabel} (Low)`,
							data: this.getDataFromSimulation('low_range').map(units.baumeToBrix),
							fill: false,
							borderColor: 'rgba(63, 132, 121, 0.4)',
							backgroundColor: 'rgba(63, 132, 121, 0.4)',
							pointBorderColor: 'rgba(63, 132, 121, 0.4)',
							pointBackgroundColor: 'rgba(63, 132, 121, 0.4)',
							pointBorderWidth: 1,
							pointRadius: 0,
							pointHoverRadius: 0,
							yAxisID: 'y1'
						});
					}

					datasets.push({
						label: 'Active Cells',
						data: this.getDataFromSimulation('active_cell_mass'),
						fill: false,
						borderColor: 'rgba(229, 201, 87, 0.8)',
						backgroundColor: 'rgba(229, 201, 87, 0.8)',
						pointBorderColor: 'rgba(229, 201, 87, 0.8)',
						pointBackgroundColor: 'rgba(229, 201, 87, 0.8)',
						pointBorderWidth: 1,
						pointRadius: 0,
						pointHoverRadius: 0,
						yAxisID: 'y1'
					},
					{
						label: 'Ferment Rate',
						data: this.getDataFromSimulation('ferment_derivative').map(units.baumeToBrix),
						fill: false,
						borderColor: 'rgba(153, 120, 188, .8)',
						backgroundColor: 'rgba(153, 120, 188, .8)',
						pointBorderColor: 'rgba(153, 120, 188, .8)',
						pointBackgroundColor: 'rgba(153, 120, 188, .8)',
						pointRadius: 0,
						pointHoverRadius: 0,
						yAxisID: 'y2'
					});
				}

				return datasets;
			},

			getXAxisLength() {
				let length = 0;

				if (this.baselineSimulation) {
					length = this.baselineSimulation.model.baume.slice(-1)[0].day;
				}

				if (! this.baselineSimulation || length > this.simulation.model.baume.slice(-1)[0].day) {
					length = this.simulation.model.baume.slice(-1)[0].day;
				}

				return length;
			},

			getDataFromSimulation(type, data = null) {
				if (! data) {
					data = this.simulation;
				}

				let xAxisLength = this.getXAxisLength();

				return data.model[type].filter(point => point.day <= xAxisLength).map(point => {
					if (point.value !== null) { // detect 0
						return parseFloat(point.value).toFixed(4);
					}

					return null;
				});
			}
		}
	}
</script>

<style>
.loading-graph {
	position: relative;
}
</style>



// WEBPACK FOOTER //
// graph.vue?1ec34bd9